// Shared forecast → plant-stress evaluation. Used on /scan and /plants.

/**
 * Compare one daily-forecast entry against a plant's thresholds. Returns an
 * array of short human-readable breach strings (e.g. ["heat 32° > max 28°"]).
 * Daily forecast only exposes temp min/max, UV max, and precipitation sum, so
 * we only check those axes here.
 */
export function evaluateDayStress(day, thresholds) {
  if (!day || !thresholds) return [];
  const out = [];
  const { temp_c_min, temp_c_max, uv_index_max, precip_mm_24h_max } = thresholds;
  if (temp_c_max != null && day.temp_max_c != null && day.temp_max_c > temp_c_max) {
    out.push(`heat ${Math.round(day.temp_max_c)}° > ${Math.round(temp_c_max)}°`);
  }
  if (temp_c_min != null && day.temp_min_c != null && day.temp_min_c < temp_c_min) {
    out.push(`cold ${Math.round(day.temp_min_c)}° < ${Math.round(temp_c_min)}°`);
  }
  if (uv_index_max != null && day.uv_index_max != null && day.uv_index_max > uv_index_max) {
    out.push(`UV ${day.uv_index_max.toFixed(1)} > ${uv_index_max.toFixed(1)}`);
  }
  if (precip_mm_24h_max != null && day.precipitation_mm != null && day.precipitation_mm > precip_mm_24h_max) {
    out.push(`rain ${Math.round(day.precipitation_mm)}mm > ${Math.round(precip_mm_24h_max)}mm`);
  }
  return out;
}

/**
 * Find the first upcoming day in `daily` where the plant's thresholds break.
 * Returns `{ date, breaches, offsetDays }` or null if the next 7 days are safe.
 */
export function nextStressDay(daily, thresholds) {
  if (!daily?.length || !thresholds) return null;
  for (let i = 0; i < daily.length; i++) {
    const breaches = evaluateDayStress(daily[i], thresholds);
    if (breaches.length) {
      return { date: daily[i].date, breaches, offsetDays: i };
    }
  }
  return null;
}

/**
 * Human weekday label from an ISO date. Uses "Today" / "Tomorrow" when the
 * date is 0 or 1 days from the first forecast day (idx 0 = today).
 */
export function forecastDayLabel(iso, offsetDays) {
  if (offsetDays === 0) return "Today";
  if (offsetDays === 1) return "Tomorrow";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short" });
  } catch {
    return iso;
  }
}
